import { useEffect } from 'react';
import { useLazyGetMatterAboutQuery } from '@/api/matter/matterAbout';
import { useAppDispatch } from '@/store';
import { removeTranscribingFiles } from '@/store/slices/cases/casesList';
import { readAllTranscribingFiles, writeTranscribingFiles } from '../utils/transcribingFilesStorage';
import { getTranscriptionOutcome, type TranscriptionOutcome } from '../utils/transcriptionOutcome';
import { showTranscriptionOutcomeToast } from '../utils/showTranscriptionOutcomeToast';

export const TRANSCRIPTION_OUTCOME_CHECK_INTERVAL_MS = 5000;

/**
 * Shows a toast on any page when a transcription requested from this browser completes or fails,
 * and stops showing its recording as "Transcribing".
 */
export const useTranscriptionOutcomeToasts = (enabled: boolean) => {
  const dispatch = useAppDispatch();
  const [getMatterAbout] = useLazyGetMatterAboutQuery();

  useEffect(() => {
    if (!enabled) return;

    const announce = async (caseId: string, outcome: TranscriptionOutcome) => {
      // The toast still says what happened when the case can't be looked up.
      const caseReference = await getMatterAbout({ matterId: caseId }, true)
        .unwrap()
        .then(
          (data) => data.info.caseReference,
          () => undefined,
        );
      showTranscriptionOutcomeToast({ caseId, caseReference, outcome });
    };

    const check = () => {
      const now = Date.now();
      Object.entries(readAllTranscribingFiles(now)).forEach(([caseId, files]) => {
        const finished = Object.entries(files).flatMap(([documentId, requestedAt]) => {
          const outcome = getTranscriptionOutcome(documentId, requestedAt, now);
          return outcome ? [{ documentId, outcome }] : [];
        });
        if (finished.length === 0) return;

        const documentIds = finished.map(({ documentId }) => documentId);
        const running = Object.fromEntries(Object.entries(files).filter(([id]) => !documentIds.includes(id)));
        writeTranscribingFiles(caseId, running);
        dispatch(removeTranscribingFiles({ caseId, documentIds }));
        finished.forEach(({ outcome }) => void announce(caseId, outcome));
      });
    };

    check();
    const intervalId = window.setInterval(check, TRANSCRIPTION_OUTCOME_CHECK_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [dispatch, enabled, getMatterAbout]);
};
